import type { ApiError, ErrorCode } from '@portal-contract';

/** Every error code of the contract plus the two the browser can produce on its own. */
export type ClientErrorCode = ErrorCode | 'network' | 'unknown';

const KNOWN_CODES: ReadonlySet<string> = new Set<ErrorCode>([
  'invalid_request',
  'invalid_email',
  'bad_origin',
  'csrf_failed',
  'no_challenge',
  'invalid_code',
  'code_expired',
  'too_many_attempts',
  'rate_limited',
  'unauthenticated',
  'session_expired',
  'not_found',
  'pdf_not_available',
  'portal_unavailable',
  'internal',
]);

/** A failed API call. `code` is the contract error code, `network` when no response arrived. */
export class ApiClientError extends Error {
  readonly status: number;
  readonly code: ClientErrorCode;
  readonly retryAfterSeconds: number | undefined;
  readonly attemptsRemaining: number | undefined;
  readonly unavailableMessage: { en: string; es: string } | undefined;

  constructor(status: number, code: ClientErrorCode, body?: ApiError | null) {
    super(`portal api error: ${code} (${status})`);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.retryAfterSeconds = typeof body?.retryAfterSeconds === 'number' ? body.retryAfterSeconds : undefined;
    this.attemptsRemaining = typeof body?.attemptsRemaining === 'number' ? body.attemptsRemaining : undefined;
    this.unavailableMessage = isMessage(body?.message) ? body.message : undefined;
  }

  static fromResponse(status: number, body: unknown): ApiClientError {
    const b = body && typeof body === 'object' ? (body as Partial<ApiError>) : null;
    const code = typeof b?.error === 'string' && KNOWN_CODES.has(b.error) ? (b.error as ErrorCode) : 'unknown';
    return new ApiClientError(status, code, b as ApiError | null);
  }
}

function isMessage(m: unknown): m is { en: string; es: string } {
  return !!m && typeof m === 'object' && typeof (m as { en?: unknown }).en === 'string' && typeof (m as { es?: unknown }).es === 'string';
}

export function isApiError(e: unknown, ...codes: ClientErrorCode[]): e is ApiClientError {
  return e instanceof ApiClientError && (codes.length === 0 || codes.includes(e.code));
}

/** Errors that mean the session is gone or the portal is off: handled once, globally, by the SessionProvider. */
export function isSessionLevelError(e: unknown): e is ApiClientError {
  return isApiError(e, 'unauthenticated', 'session_expired', 'portal_unavailable');
}

type Listener = (e: ApiClientError) => void;
const listeners = new Set<Listener>();

/** Tiny bus: query and mutation caches report failures here; the SessionProvider routes the session-level ones. */
export const apiErrorBus = {
  subscribe(l: Listener): () => void {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  emit(e: unknown): void {
    if (e instanceof ApiClientError) for (const l of [...listeners]) l(e);
  },
};
